import { useMutation } from '@api/hooks'
import { RHFCheckbox, RHFRadioGroup } from '@components/RHFInputs'
import { DevTool } from '@hookform/devtools'
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Button, Divider, Drawer, Skeleton, Stack, Typography } from '@mui/material'
import MarketBasketsApi from '@services/marketBaskets.api'
import { CreateMarketBasketPdfExportReq } from '@services/marketBaskets.types'
import { isLowerEnvironment } from '@utils/EnvironmentHelper'
import { saveBlob } from '@utils/helpers'
import { useMarketBasketContext } from '@views/MarketBaskets/MarketBaskets.hooks'
import { AxiosError } from 'axios'
import { FormProvider, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { z } from 'zod'

import { pdfExportSchema } from './BasketExport.schema'
import { pdfExportDefaults } from './BasketExport.utils'

type Props = {
	open: boolean
	onClose: () => void
}

type Schema = z.infer<typeof pdfExportSchema>

/**
 * Displays a drawer to export a PDF basket report.
 */
export default function PdfExport({ open, onClose }: Props) {
	const { marketBasket, isLoading } = useMarketBasketContext()

	const { mutate, isPending } = useMutation({
		mutationFn: MarketBasketsApi.exportPdf,
		onSuccess: (blob: Blob) => {
			saveBlob(blob, `${marketBasket?.displayName || `market_basket_${marketBasket?.id}`}_summary`)
			onClose()
		},
		// response is a Blob, so we need to parse the error message differently
		onError: async (error: AxiosError<Blob>) => {
			const text = await error.response.data.text()
			const json = JSON.parse(text)
			toast.error(`${json.message}`)
		},
	})

	const handleSubmit = (values: Schema) => {
		const settings = {
			markComplete: values.markComplete,
			includeAllPurchases: values.purchasesToInclude !== 'IN_BASKET',
		}

		mutate({
			id: marketBasket.id,
			settings,
		} as CreateMarketBasketPdfExportReq)
	}

	const methods = useForm<Schema>({
		mode: 'onBlur',
		resolver: zodResolver(pdfExportSchema),
		shouldFocusError: true,
		defaultValues: pdfExportDefaults,
	})

	return (
		<Drawer open={open} variant="temporary" anchor="right" onClose={onClose}>
			<FormProvider {...methods}>
				<form id="pdf-export" onSubmit={methods.handleSubmit(handleSubmit)}>
					<Stack spacing={2} sx={{ width: '48.125rem', p: 2 }}>
						<Stack spacing={2} direction="row" sx={{ width: '100%', mb: 2 }}>
							<Box sx={{ width: '100%' }}>
								<Typography variant="h6">{`Export to PDF (${marketBasket?.salesforceCaseNumber || ''})`}</Typography>
							</Box>
							<Stack spacing={2} direction="row" sx={{ width: '100%', justifyContent: 'flex-end' }}>
								<Button variant="text" onClick={onClose} color="secondary" disabled={isPending}>
									Cancel
								</Button>
								<Button type="submit" form="pdf-export" variant="contained" loading={isPending}>
									Export
								</Button>
							</Stack>
						</Stack>

						<Typography variant="body1">
							{isLoading ? <Skeleton /> : marketBasket?.displayName || ''}
						</Typography>

						<Divider sx={{ pb: 1 }} />

						{/* <Include purchases /> */}
						<RHFRadioGroup
							row
							fieldLabelPlacement="row"
							name="purchasesToInclude"
							label="Include purchases that are"
							size="small"
							options={[
								{ value: 'IN_BASKET', label: 'In the Basket' },
								{ value: 'ALL', label: 'All purchases' },
							]}
						/>

						<Stack spacing={2} direction="row" sx={{ alignItems: 'center' }}>
							<RHFCheckbox name="markComplete" />
							<Typography variant="body2">Complete Market Basket on Export</Typography>
						</Stack>
					</Stack>
				</form>
				{isLowerEnvironment && <DevTool control={methods.control} placement="top-right" />}
			</FormProvider>
		</Drawer>
	)
}
